import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';

interface CameraService {
  isNativeApp: boolean;
  hasCamera: boolean;
  networkStatus: any;
}

class NativeCameraService implements CameraService {
  public isNativeApp: boolean = false;
  public hasCamera: boolean = false;
  public networkStatus: any = null;

  constructor() {
    this.initializeService();
  }

  private async initializeService() {
    try {
      // Check if running in native app
      const deviceInfo = await Device.getInfo();
      this.isNativeApp = deviceInfo.platform !== 'web';
      
      // Check camera availability
      this.hasCamera = await this.checkCameraAvailability();
      
      // Monitor network status
      this.networkStatus = await Network.getStatus();
      Network.addListener('networkStatusChange', (status) => {
        this.networkStatus = status;
        console.log('[NativeCamera] Network status changed:', status);
      });

      console.log('[NativeCamera] Service initialized:', {
        isNative: this.isNativeApp,
        hasCamera: this.hasCamera,
        network: this.networkStatus
      });
    } catch (error) {
      console.error('[NativeCamera] Initialization failed:', error);
    }
  }

  private async checkCameraAvailability(): Promise<boolean> {
    try {
      if (this.isNativeApp) {
        // For native app, assume camera is available
        // Actual permission check will happen when taking photo
        return true;
      } else {
        // For web, check MediaDevices API
        return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      }
    } catch (error) {
      console.error('[NativeCamera] Camera check failed:', error);
      return false;
    }
  }

  async takePhoto(): Promise<{ success: boolean; photo?: Photo | File; error?: string }> {
    try {
      if (this.isNativeApp) {
        return await this.takeNativePhoto();
      } else {
        return await this.takeWebPhoto();
      }
    } catch (error: any) {
      console.error('[NativeCamera] Photo capture failed:', error);
      return {
        success: false,
        error: error.message || 'Unknown camera error'
      };
    }
  }

  private async takeNativePhoto(): Promise<{ success: boolean; photo?: Photo; error?: string }> {
    try {
      console.log('[NativeCamera] Taking native photo...');
      
      const photo = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        width: 640,
        height: 480,
        correctOrientation: true,
        saveToGallery: false
      });

      console.log('[NativeCamera] Native photo captured successfully');
      return { success: true, photo };
    } catch (error: any) {
      console.error('[NativeCamera] Native photo failed:', error);
      
      let errorMessage = 'Camera access failed';
      if (error.message?.includes('permission')) {
        errorMessage = 'Camera permission denied. Please enable camera access in app settings.';
      } else if (error.message?.includes('cancel')) {
        errorMessage = 'Photo capture cancelled by user.';
      }
      
      return { success: false, error: errorMessage };
    }
  }

  private async takeWebPhoto(): Promise<{ success: boolean; photo?: File; error?: string }> {
    try {
      console.log('[NativeCamera] Taking web photo...');
      
      // This would integrate with your existing web camera code
      // For now, return an error to indicate web fallback needed
      return {
        success: false,
        error: 'Web camera not implemented in this service. Use existing web camera code.'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Web camera error'
      };
    }
  }

  async requestPermissions(): Promise<{ success: boolean; error?: string }> {
    try {
      if (this.isNativeApp) {
        const permissions = await Camera.requestPermissions({
          permissions: ['camera', 'photos']
        });
        
        if (permissions.camera === 'granted') {
          return { success: true };
        } else {
          return { 
            success: false, 
            error: 'Camera permission denied. Please enable in app settings.' 
          };
        }
      } else {
        // For web, permissions are handled by browser
        return { success: true };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Permission request failed'
      };
    }
  }

  async checkPermissions(): Promise<{ camera: string; photos: string }> {
    try {
      if (this.isNativeApp) {
        const permissions = await Camera.checkPermissions();
        return {
          camera: permissions.camera || 'prompt',
          photos: permissions.photos || 'prompt'
        };
      } else {
        return {
          camera: 'granted', // Assume granted for web
          photos: 'granted'
        };
      }
    } catch (error) {
      console.error('[NativeCamera] Permission check failed:', error);
      return {
        camera: 'prompt',
        photos: 'prompt'
      };
    }
  }

  // Convert Capacitor photo to File for upload
  async photoToFile(photo: Photo, filename: string = 'photo.jpg'): Promise<File> {
    if (!photo.dataUrl) {
      throw new Error('Photo data not available');
    }

    // Convert data URL to blob
    const response = await fetch(photo.dataUrl);
    const blob = await response.blob();
    
    // Create File object
    return new File([blob], filename, { type: 'image/jpeg' });
  }

  // Get device information for debugging
  async getDeviceInfo() {
    try {
      const deviceInfo = await Device.getInfo();
      return {
        platform: deviceInfo.platform,
        model: deviceInfo.model,
        operatingSystem: deviceInfo.operatingSystem,
        osVersion: deviceInfo.osVersion,
        manufacturer: deviceInfo.manufacturer,
        isVirtual: deviceInfo.isVirtual,
        webViewVersion: deviceInfo.webViewVersion
      };
    } catch (error) {
      console.error('[NativeCamera] Device info failed:', error);
      return null;
    }
  }

  // Check if online for photo upload
  isOnline(): boolean {
    return this.networkStatus?.connected || false;
  }

  // Get network type
  getNetworkType(): string {
    return this.networkStatus?.connectionType || 'unknown';
  }
}

// Create singleton instance
const nativeCameraService = new NativeCameraService();

export default nativeCameraService;
export { NativeCameraService };
export type { CameraService };
